// @ts-check
import React, { useCallback } from 'react';
import { useMutation } from 'react-query';
import api from 'utils/api';
import { Modal, ModalHeader } from 'preferral-ui/components/Modal';
import preferralLogoSrc from 'images/preferral_logo_small.png';
import aristamdLogoSrc from 'images/aristamd_logo.svg';
import { FAIcon } from 'preferral-ui/components/FAIcon';
import { Button } from 'preferral-ui/components/Button';

/** @typedef {{
  isOpen: boolean;
  onClose(): void;
}} RebrandNoticeModalProps */

/** @type { React.FC<RebrandNoticeModalProps> } */
export const RebrandNoticeModal = (props) => {
  const { isOpen, onClose } = props;

  const mutation = useMutation(() =>
    api
      .put(`/agent_portal/api/acknowledge_rebrand`, {})
      .then((resp) => resp.data)
  );

  const dismiss = useCallback(() => {
    return mutation.mutateAsync().then(() => onClose());
  }, [mutation, onClose]);

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="max-w-3xl">
      <ModalHeader icon="bullhorn" title="Announcement" onClose={onClose} />

      <div className="_RebrandNoticeModal px-4 pb-6">
        <div className="gap-x-3 flex items-center justify-center">
          <img src={preferralLogoSrc} alt="Preferral Logo" />
          <div className="mx-4 text-3xl text-gray-700">
            <FAIcon icon="arrow-right" />
          </div>
          <img src={aristamdLogoSrc} alt="AristaMD Logo" />
        </div>

        <div className="pt-8">
          <h3 className="text-2xl font-bold">
            Preferral has merged with AristaMD
          </h3>

          <p className="pt-6">
            <a
              href="https://www.aristamd.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              AristaMD
            </a>
            , a leading digital health company that provides eConsult solutions
            to connect primary care providers with timely, documented specialist
            insights, has acquired the Preferral platform.
          </p>

          <p className="pt-6">
            Going forward, you'll notice updated AristaMD branding throughout
            the app. In addition, we're working on a number of exciting new
            features to seamlessly incorporate AristaMD's leading eConsultation
            services, as well as a number of enhancements to the core Referral
            Management platform.
          </p>

          <p className="pt-6">
            The press release can be{' '}
            <a
              href="https://www.aristamd.com/press/aristamd-acquires-referral-management-solution-preferral-to-increase-patient-access-to-specialty-care/"
              target="_blank"
              rel="noopener noreferrer"
            >
              found here
            </a>
            . As always, please don't hesitate to reach out with any thoughts or
            questions. Thank you for your continued partnership, and we're
            excited for what's ahead!
          </p>

          <p className="pt-6 italic">- The Preferral Team</p>

          <div className="pt-6 text-center">
            <Button
              kind="primary"
              color="blue"
              size="lg"
              isLoading={mutation.isLoading}
              disabled={mutation.isLoading}
              onClick={dismiss}
            >
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
